import { useState } from 'react'
import { api } from '../lib/api'
import { maskName } from '../lib/security'
import type { Person } from '../lib/types'

type Kind = 'parent-of' | 'spouse-of' | 'child-of' | 'sibling-of'
const ROLES: { kind: Kind; label: string }[] = [
  { kind: 'parent-of', label: 'Parent' },
  { kind: 'spouse-of', label: 'Spouse' },
  { kind: 'child-of', label: 'Child' },
  { kind: 'sibling-of', label: 'Sibling' },
]

/**
 * Link an EXISTING tree member to the selected person — no new person, and the
 * search is scoped ONLY to this tree's people (never a global/app-wide query).
 * Produces the exact same bonds as the +Add flows via the shared server logic.
 */
export default function LinkKinPanel({ treeId, selected, persons, veil, onDone, onCancel }: {
  treeId: string
  selected: Person
  persons: Person[]
  veil: boolean
  onDone: () => void
  onCancel: () => void
}) {
  const [q, setQ] = useState('')
  const [pick, setPick] = useState<Person | null>(null)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')

  const candidates = persons
    .filter(p => p.id !== selected.id)
    .filter(p => !q.trim() || maskName(p, false).toLowerCase().includes(q.trim().toLowerCase()) || `${p.first_name} ${p.last_name || ''}`.toLowerCase().includes(q.trim().toLowerCase()))
    .slice(0, 40)

  const merge = async () => {
    if (!pick) return
    if (!confirm(`Merge “${maskName(pick, veil)}” INTO “${maskName(selected, veil)}”?\n\nEverything ${maskName(pick, veil)} has — bonds, photos, stories — moves to ${maskName(selected, veil)}, empty details are filled in, and the duplicate is permanently removed. This cannot be undone.`)) return
    setBusy(true); setMsg('')
    try {
      await api.mergePersons(treeId, selected.id, pick.id)
      onDone()
    } catch (e) {
      setMsg(e instanceof Error && e.message.includes('409') ? 'Both are linked to different accounts — unlink one first.' : 'Merge failed — try again.')
      setBusy(false)
    }
  }

  const link = async (kind: Kind) => {
    if (!pick) return
    setBusy(true); setMsg('')
    try {
      await api.linkKin(treeId, kind, pick.id, selected.id)
      onDone()
    } catch (e) {
      setMsg(e instanceof Error && e.message.includes('already') ? 'They’re already linked that way.' : 'Could not link — try again.')
      setBusy(false)
    }
  }

  return (
    <div className="mt-3 rounded-xl border border-rootv-500/25 bg-rootv-600/[0.06] p-3">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-slate-200">🔗 Link an existing person</p>
        <button onClick={onCancel} className="text-[11px] text-slate-500 hover:text-slate-300">close</button>
      </div>

      {!pick ? (
        <>
          <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search this tree…"
            className="input-dark !py-1.5 text-xs w-full mb-2" />
          <div className="max-h-44 overflow-y-auto space-y-1">
            {candidates.map(p => (
              <button key={p.id} onClick={() => setPick(p)}
                className="w-full text-left px-2.5 py-1.5 rounded-lg bg-white/[0.03] border border-white/[0.06] hover:border-rootv-500/40 text-xs text-slate-200">
                {maskName(p, veil)}
                <span className="text-slate-600"> {p.birth_date ? `· b.${String(p.birth_date).slice(0, 4)}` : ''}{p.is_living ? '' : ' ✝'}</span>
              </button>
            ))}
            {candidates.length === 0 && <p className="text-[11px] text-slate-500 text-center py-3">No one else in this tree{q ? ' matches' : ''}.</p>}
          </div>
        </>
      ) : (
        <>
          <p className="text-xs text-slate-300 mb-2">Link <b>{maskName(pick, veil)}</b> as {maskName(selected, veil)}’s…</p>
          <div className="grid grid-cols-4 gap-1.5">
            {ROLES.map(r => (
              <button key={r.kind} disabled={busy} onClick={() => link(r.kind)}
                className="text-[11px] px-2 py-1.5 rounded-lg border border-rootv-500/30 text-rootv-300 hover:bg-rootv-500/10 disabled:opacity-50">
                {r.label}
              </button>
            ))}
          </div>
          <div className="mt-2 pt-2 border-t border-white/[0.06]">
            <button disabled={busy} onClick={merge}
              className="text-[11px] px-2 py-1.5 rounded-lg border border-amber-500/30 text-amber-300 hover:bg-amber-500/10 disabled:opacity-50 w-full">
              ⇄ …or merge {maskName(pick, veil)} into {maskName(selected, veil)} (they're the same person)
            </button>
          </div>
          <button onClick={() => { setPick(null); setMsg('') }} className="text-[11px] text-slate-500 hover:text-slate-300 mt-2">← pick someone else</button>
        </>
      )}
      {msg && <p className="text-[11px] text-amber-400 mt-2">{msg}</p>}
    </div>
  )
}
